import { z } from 'zod';

/**
 * Pitch Mode — the business-thinking thread.
 *
 * Unlocks only after a child has shipped a few games, because a pitch for
 * something that does not exist is a daydream, and the point here is the
 * opposite: attaching numbers to a real thing you actually made.
 *
 * The valuable part is not the questions, it is the arithmetic. A child who sets
 * a price and is immediately shown how many people would have to buy it — and
 * then compares that to how many people they actually know — has met the real
 * constraint on every small business, which is distribution rather than the
 * idea. That lesson lands at nine and keeps.
 */

export const PITCH_UNLOCK_AFTER_GAMES = 3;

export const PitchSchema = z.object({
  /** Who is it for? Not "everyone" — the form pushes back on that. */
  audience: z.string().trim().min(1).max(200),
  /** One sentence that would make them want it. */
  hook: z.string().trim().min(1).max(200),
  /** Price in AED. Zero is allowed: "free" is a real business model. */
  priceAed: z.number().min(0).max(1000),
  /** What they would do with the money. */
  plan: z.string().trim().max(300).default(''),
  /** Roughly how many people they could actually tell. Grounds the maths. */
  peopleTheyKnow: z.number().int().min(0).max(10000).default(0),
});

export type Pitch = z.infer<typeof PitchSchema>;

/** The target a child is working towards. Concrete and reachable-feeling. */
export const GOAL_AED = 500;

export type PitchMaths = {
  goalAed: number;
  priceAed: number;
  /** null when the game is free — no number of sales reaches the goal. */
  salesNeeded: number | null;
  /** How many people they know, for comparison. */
  peopleTheyKnow: number;
  /** Could they reach it by telling everyone they know? */
  reachableFromPeopleTheyKnow: boolean;
  /** What fraction of everyone they know would have to buy. Null if free. */
  shareOfPeopleNeeded: number | null;
  /** A few alternative prices, so the tradeoff is visible rather than argued. */
  atOtherPrices: { priceAed: number; salesNeeded: number }[];
};

const OTHER_PRICES = [2, 5, 10, 20, 50];

export function pitchMaths(pitch: Pitch, goalAed = GOAL_AED): PitchMaths {
  const salesNeeded = pitch.priceAed > 0 ? Math.ceil(goalAed / pitch.priceAed) : null;

  return {
    goalAed,
    priceAed: pitch.priceAed,
    salesNeeded,
    peopleTheyKnow: pitch.peopleTheyKnow,
    reachableFromPeopleTheyKnow:
      salesNeeded !== null && pitch.peopleTheyKnow > 0 && salesNeeded <= pitch.peopleTheyKnow,
    shareOfPeopleNeeded:
      salesNeeded !== null && pitch.peopleTheyKnow > 0
        ? Math.round((salesNeeded / pitch.peopleTheyKnow) * 100) / 100
        : null,
    atOtherPrices: OTHER_PRICES.filter((p) => p !== pitch.priceAed).map((priceAed) => ({
      priceAed,
      salesNeeded: Math.ceil(goalAed / priceAed),
    })),
  };
}

/**
 * What to say back about the numbers.
 *
 * Never flattery and never discouragement — just the consequence of the number
 * they chose, stated plainly so they can decide what to do about it. Being told
 * "you'd need to sell to eight times everyone you know" is more useful, and more
 * respectful, than "great pitch!".
 */
export function pitchVerdict(maths: PitchMaths): string {
  if (maths.salesNeeded === null) {
    return `Free means anyone can play it — but you would not reach ${maths.goalAed} AED that way. Some people give the game away and charge for something else.`;
  }
  if (maths.peopleTheyKnow === 0) {
    return `At ${maths.priceAed} AED you would need ${maths.salesNeeded} people to buy it. How many people could you actually tell?`;
  }
  if (maths.reachableFromPeopleTheyKnow) {
    return `At ${maths.priceAed} AED you need ${maths.salesNeeded} buyers, and you know about ${maths.peopleTheyKnow} people. That is actually possible — if enough of them say yes.`;
  }
  const times = Math.round((maths.salesNeeded / maths.peopleTheyKnow) * 10) / 10;
  return `At ${maths.priceAed} AED you would need ${maths.salesNeeded} buyers — about ${times} times everyone you know. So either the price goes up, or you need a way to reach people you have never met. That second one is the hard part of every business.`;
}

/** "Everyone" is not an audience, and noticing that is most of the lesson. */
const VAGUE_AUDIENCES = /^(everyone|everybody|all people|anyone|people|kids|children|the world)\.?$/i;

export const audienceIsVague = (audience: string): boolean =>
  VAGUE_AUDIENCES.test(audience.trim());
